import { memo } from "react";
import { ActivityIndicator } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { PressableScale } from "pressto";
import { Button, Card, Text, View } from "@src/components";
import { Icon } from "@src/components/Icon";
import { AlarmStateBadge } from "./AlarmStateBadge";
import { DiagonalStripeSquare } from "@src/features/schedules/components/DiagonalStripeSquare";
import { useSiteCameraKeys } from "@src/features/cameras/hooks/useSiteCameraKeys";
import { useExceptionSuggestions } from "@src/services/api/hooks";
import { capitalize } from "@src/utils/capitalize";
import { makeDateTimeRecord } from "@visioni/schedules";
import type { FC } from "react";
import type { AlarmExceptionSuggestion } from "@src/services/api/types";
import { formatSuggestionDate, buildPeriodFromSuggestion } from "@src/features/alarms/utils/exception-format";

interface CreateExceptionContentProps {
  onSelectSuggestion: (exception: { reason: string; action: string; period: unknown }) => void;
  onCreateNew: (params: { exception: string; isNew: string; allCameraKeys: string }) => void;
}

export const CreateExceptionContent: FC<CreateExceptionContentProps> = memo(({ onSelectSuggestion, onCreateNew }) => {
  const { theme } = useUnistyles();
  const { cameraKeys, selectedSiteId } = useSiteCameraKeys();
  const firstCameraKey = cameraKeys[0];
  const effectiveSiteId = selectedSiteId ?? "";

  const suggestionsQuery = useExceptionSuggestions(effectiveSiteId, firstCameraKey);
  const suggestions = suggestionsQuery.data ?? [];
  const isLoading = suggestionsQuery.isLoading;

  const handleSelectSuggestion = (suggestion: AlarmExceptionSuggestion) => {
    onSelectSuggestion({
      reason: suggestion.reason,
      action: suggestion.action,
      period: buildPeriodFromSuggestion(suggestion),
    });
  };

  const handleCreateNew = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();

    const exception = {
      reason: "",
      action: "armed",
      period: {
        start: makeDateTimeRecord(year, month, day, 0, 0),
        end: makeDateTimeRecord(year, month, day, 24, 0),
      },
    };

    onCreateNew({
      exception: JSON.stringify(exception),
      isNew: "true",
      allCameraKeys: JSON.stringify(cameraKeys),
    });
  };

  if (isLoading) {
    return (
      <View flex={1} justifyContent="center" alignItems="center">
        <ActivityIndicator color={theme.colors.text} />
      </View>
    );
  }

  return (
    <View gap="3xl" flex={1}>
      {suggestions.length > 0 && (
        <View>
          <Text textStyle="xs/medium" color="textMuted" mb="lg">
            Suggested
          </Text>
          <View gap="lg">
            {suggestions.map((suggestion) => (
              <SuggestionCard
                key={suggestion.id}
                suggestion={suggestion}
                onPress={() => handleSelectSuggestion(suggestion)}
              />
            ))}
          </View>
        </View>
      )}

      <Button
        label="Create new"
        variant="primary"
        variantColor="blue"
        size="lg"
        onPress={handleCreateNew}
        iconRight={<Icon name="plus" color={theme.colors.white.default} size={20} />}
      />
    </View>
  );
});

CreateExceptionContent.displayName = "CreateExceptionContent";

const SuggestionCard: FC<{ suggestion: AlarmExceptionSuggestion; onPress: () => void }> = memo(
  ({ suggestion, onPress }) => {
    const { theme } = useUnistyles();
    const { date, time } = formatSuggestionDate(suggestion);

    return (
      <PressableScale onPress={onPress}>
        <Card>
          <View flexDirection="row" justifyContent="space-between" gap="md">
            <View flex={1}>
              <View flexDirection="row" alignItems="center" gap="md" mb="2xl">
                <View style={{ overflow: "hidden" }}>
                  <DiagonalStripeSquare state={suggestion.action as "armed" | "disarmed"} size={20} />
                </View>
                <Text textStyle="sm/medium" numberOfLines={2} ellipsizeMode="tail" style={{ flexShrink: 1 }}>
                  {suggestion.reason}
                </Text>
                <AlarmStateBadge state={suggestion.action} />
              </View>
              <View flexDirection="row" alignItems="center" gap="xs">
                <Icon name="calendarOutline" color={theme.colors.textMuted} size={16} />
                <Text textStyle="xs/regular" color="textMuted">
                  {date}
                </Text>
                {time && (
                  <>
                    <Text textStyle="xs/regular" color="textMuted">
                      •
                    </Text>
                    <Icon name="clockOutline" color={theme.colors.textMuted} size={16} />
                    <Text textStyle="xs/regular" color="textMuted">
                      {capitalize(time)}
                    </Text>
                  </>
                )}
              </View>
            </View>
            <Icon name="chevronRight" color={theme.colors.textMuted} size={20} />
          </View>
        </Card>
      </PressableScale>
    );
  },
);

SuggestionCard.displayName = "SuggestionCard";
